import { useCallback, useEffect, useState } from 'react';

// Spalten-Konfiguration pro Nutzer in localStorage.
// Schluessel: tracking-dashboard-cols-{userId}.
// Bei unbekanntem User oder fehlendem Eintrag werden die Defaults
// aus der ColumnDef-Liste benutzt (Aufrufer-Verantwortung).

export type DashboardSettings = {
  visibleColumns: string[];
};

function storageKey(userId: number | null): string {
  return `tracking-dashboard-cols-${userId ?? 'anon'}`;
}

function safeParse(raw: string | null): DashboardSettings | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      Array.isArray((parsed as DashboardSettings).visibleColumns) &&
      (parsed as DashboardSettings).visibleColumns.every((c) => typeof c === 'string')
    ) {
      return parsed as DashboardSettings;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function useDashboardSettings(userId: number | null, defaults: DashboardSettings) {
  const [settings, setSettings] = useState<DashboardSettings>(defaults);

  // Initial laden + bei userId-Wechsel neu laden.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const fromLs = safeParse(window.localStorage.getItem(storageKey(userId)));
    setSettings(fromLs ?? defaults);
    // defaults absichtlich nicht in deps — wir wollen nur bei userId neu laden
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const setVisibleColumns = useCallback(
    (cols: string[]) => {
      setSettings((prev) => {
        const next = { ...prev, visibleColumns: cols };
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(storageKey(userId), JSON.stringify(next));
        }
        return next;
      });
    },
    [userId]
  );

  return { settings, setVisibleColumns };
}
